import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShoppingBag, Lock, X } from "lucide-react";
import { useCart } from "../context/CartContext";
import { INFO } from "../data";

const EXIT_CODE = "671414";

export default function Kiosk() {
  const navigate = useNavigate();
  const { clear } = useCart();
  const [exitOpen, setExitOpen] = useState(false);
  const [code, setCode] = useState("");
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("kds_token")) {
      navigate("/kds/login?next=/kiosk");
      return;
    }
    localStorage.setItem("kiosk_mode", "1");
    clear(); // fresh start for each customer
  }, [clear, navigate]);

  const start = () => navigate("/order");

  const tryExit = () => {
    if (code === EXIT_CODE) {
      localStorage.removeItem("kiosk_mode");
      navigate("/kds");
    } else {
      setErr(true);
      setCode("");
    }
  };

  return (
    <div
      onClick={start}
      data-testid="kiosk-attract"
      className="relative min-h-screen bg-black text-white flex flex-col items-center justify-center px-6 cursor-pointer overflow-hidden"
    >
      <div className="grain-overlay" aria-hidden="true" />
      <button
        onClick={(e) => { e.stopPropagation(); setExitOpen(true); setErr(false); setCode(""); }}
        data-testid="kiosk-exit-btn"
        className="absolute top-5 right-5 z-10 inline-flex items-center gap-2 font-body text-xs uppercase tracking-widest text-neutral-700 hover:text-neutral-400 transition-colors"
      >
        <Lock size={13} /> Staff
      </button>

      <AnimatePresence>
        {exitOpen && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={(e) => { e.stopPropagation(); setExitOpen(false); }}
            className="fixed inset-0 z-30 bg-black/90 flex items-center justify-center px-6"
          >
            <div onClick={(e) => e.stopPropagation()} data-testid="kiosk-exit-modal" className="w-full max-w-xs border border-neon-green/40 bg-elevated p-6 relative">
              <button onClick={() => setExitOpen(false)} className="absolute top-3 right-3 text-neutral-500 hover:text-white"><X size={20} /></button>
              <h3 className="font-heading text-2xl text-neon-green uppercase tracking-wide flex items-center gap-2"><Lock size={18} /> Staff Exit</h3>
              <p className="font-body text-sm text-neutral-400 mt-1">Enter staff code to exit kiosk.</p>
              <input
                autoFocus
                type="tel"
                inputMode="numeric"
                value={code}
                onChange={(e) => { setCode(e.target.value.replace(/\D/g, "")); setErr(false); }}
                onKeyDown={(e) => e.key === "Enter" && tryExit()}
                data-testid="kiosk-exit-code"
                placeholder="••••••"
                className="mt-4 w-full bg-black border border-neon-green/30 text-white text-center text-2xl tracking-[0.4em] py-3 focus:outline-none focus:border-neon-green"
              />
              {err && <p className="font-body text-sm text-red-500 mt-2" data-testid="kiosk-exit-error">Wrong code. Try again.</p>}
              <button
                onClick={tryExit}
                data-testid="kiosk-exit-confirm"
                className="mt-4 w-full font-heading text-xl tracking-wide bg-neon-green text-black py-3 hover:bg-neon-yellow transition-colors"
              >
                Exit Kiosk
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="text-center relative z-[1]"
      >
        <div className="flex items-baseline justify-center gap-3">
          <span className="font-heading text-7xl sm:text-8xl text-neon-green neon-glow-green leading-none">67</span>
          <span className="font-heading text-4xl sm:text-6xl text-neon-yellow leading-none tracking-wide">WINGS &amp; TENDERS</span>
        </div>
        <p className="font-graffiti text-neon-yellow text-4xl sm:text-5xl -rotate-2 mt-6">order here!</p>

        <motion.button
          onClick={(e) => { e.stopPropagation(); start(); }}
          data-testid="kiosk-start-btn"
          animate={{ boxShadow: ["0 0 0px rgba(57,255,20,0.0)", "0 0 40px rgba(57,255,20,0.6)", "0 0 0px rgba(57,255,20,0.0)"] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
          className="mt-12 inline-flex items-center gap-4 font-heading text-4xl sm:text-5xl tracking-wide bg-neon-green text-black px-14 py-8 hover:bg-neon-yellow transition-colors"
        >
          <ShoppingBag size={40} /> Tap To Start
        </motion.button>

        <p className="font-body text-sm text-neutral-500 mt-10 uppercase tracking-[0.25em]">
          Fresh · Never Frozen · {INFO.address}
        </p>
      </motion.div>
    </div>
  );
}
